import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useSchool } from '../../contexts/SchoolContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import toast from 'react-hot-toast';

export function PortalReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { school } = useSchool();
  const { subscription } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!school?.id) {
        setLoading(false);
        return;
      }

      try {
        // Get the latest subscription status from Firestore
        const schoolDoc = await getDoc(doc(db, 'schools', school.id));
        const subscriptionData = schoolDoc.data()?.subscription;

        if (subscriptionData) {
          // Determine what changed based on the subscription status
          if (subscriptionData.status === 'canceled' || subscriptionData.cancelAtPeriodEnd) {
            setMessage('Your subscription has been scheduled for cancellation. You will continue to have access until the end of your billing period.');
            toast.success('Subscription cancellation scheduled');
          } else if (subscriptionData.status === 'active') {
            setMessage('Your subscription settings have been updated successfully.');
            toast.success('Subscription updated');
          } else {
            setMessage('Your billing information has been updated.');
            toast.success('Billing updated');
          }
        } else {
          setMessage('Thank you for updating your billing information.');
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        setMessage('Your changes have been saved.');
      } finally {
        setLoading(false);
      }
    };

    checkSubscriptionStatus();
  }, [school?.id]);

  const handleReturn = () => {
    navigate('/billing');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Checking Status</h2>
          <p className="text-gray-600">
            Please wait while we update your subscription information...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Update Complete</h2>
        <p className="text-gray-600 mb-6">
          {message}
        </p>
        
        <button
          onClick={handleReturn}
          className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Billing
        </button>

        <p className="text-xs text-gray-500 mt-6">
          If you have any questions about your subscription, please contact our support team.
        </p>
      </div>
    </div>
  );
}

export default PortalReturn;